import {
	AttestationType,
	MetadataProperties,
	nft,
	PinnedMetadataProperties,
	TargetWallet,
} from '#types/loreTypes.tsx'
import { useNavigate } from '@remix-run/react'
import Button from '../buttons/button'
import Modal from '../modalsAndOverlays/modal'
import { TypographyHeading, TypographyParagraph } from '../typography'
import { useEffect, useState } from 'react'
import SignForm from './signForm.tsx'
import CollectionAddComponent from '../collectionAdd/collectionAdd.tsx'
import { DynamicContext } from '@dynamic-labs/sdk-react-core'

export const SignModal = ({
	open,
	onClose,
	template,
	pendingPin,
	pendingSign,
	countersignEntry,
	matchingAddress,
	isValidSign,
	nft,
	collection,
	origin,
	atomicLoreUrl,
	targetWallet,
	handleRevalidation,
}: {
	open: boolean
	onClose: () => void
	template?: string
	pendingPin?: MetadataProperties
	pendingSign?: PinnedMetadataProperties
	countersignEntry?: AttestationType
	matchingAddress?: boolean
	isValidSign?: boolean
	nft?: nft
	collection?: AttestationType
	origin: string
	atomicLoreUrl: string
	targetWallet?: TargetWallet | null
	handleRevalidation?: () => void
}) => {
	const navigate = useNavigate()
	const [confirmationModal, setConfirmationModal] = useState<{
		title: string
		message: string
		acceptButton: string
		cancelButton: string | null
	} | null>(null)
	const [pendingPinState, setPendingPinState] = useState<
		MetadataProperties | undefined
	>(pendingPin)
	const [pendingSignState, setPendingSignState] = useState<
		PinnedMetadataProperties | undefined
	>(pendingSign)
	const [signedEntry, setSignedEntry] = useState<AttestationType | null>(null)
	const [hasSigned, setHasSigned] = useState(false)
	const [leaveSignModalOpen, setLeaveSignModalOpen] = useState(false)

	const handleOnClose = () => {
		onClose()
		setTimeout(() => setHasSigned(false), 1000)
	}

	useEffect(() => {
		if (signedEntry) {
			setHasSigned(true)
			setSignedEntry(null)
			setPendingPinState(undefined)
			setPendingSignState(undefined)
			setConfirmationModal(null)
			handleRevalidation && handleRevalidation()
		}
	}, [handleRevalidation, navigate, signedEntry])

	return (
		<Modal
			size={hasSigned || leaveSignModalOpen ? 'large' : 'sm:max-w-[80%]'}
			body={
				!DynamicContext._currentValue ? (
					<div>...Loading</div>
				) : (
					<SignModalBody
						leaveSignModalOpen={leaveSignModalOpen}
						setLeaveSignModalOpen={setLeaveSignModalOpen}
						onClose={handleOnClose}
						template={template}
						countersignEntry={countersignEntry}
						matchingAddress={matchingAddress}
						isValidSign={isValidSign}
						nft={nft}
						collection={collection}
						origin={origin}
						atomicLoreUrl={atomicLoreUrl}
						setPendingPinState={setPendingPinState}
						pendingPinState={pendingPinState}
						setPendingSignState={setPendingSignState}
						pendingSignState={pendingSignState}
						setSignedEntry={setSignedEntry}
						setConfirmationModal={setConfirmationModal}
						confirmationModal={confirmationModal}
						targetWallet={targetWallet}
					/>
				)
			}
			isOpen={open}
			onClose={() => {
				if (leaveSignModalOpen) {
					setLeaveSignModalOpen(false)
				} else {
					confirmationModal ? setLeaveSignModalOpen(true) : handleOnClose()
				}
			}}
			showClose={leaveSignModalOpen ? false : true}
		/>
	)
}

const SignModalBody = ({
	leaveSignModalOpen,
	setLeaveSignModalOpen,
	onClose,
	template,
	countersignEntry,
	matchingAddress,
	isValidSign,
	nft,
	collection,
	origin,
	atomicLoreUrl,
	setPendingPinState,
	pendingPinState,
	setPendingSignState,
	pendingSignState,
	setSignedEntry,
	setConfirmationModal,
	confirmationModal,
	targetWallet,
}: {
	leaveSignModalOpen: boolean
	setLeaveSignModalOpen: (val: boolean) => void
	onClose: () => void
	template?: string
	countersignEntry?: AttestationType
	matchingAddress?: boolean
	isValidSign?: boolean
	nft?: nft
	collection?: AttestationType
	origin: string
	atomicLoreUrl: string
	setPendingPinState: (v: MetadataProperties) => void
	pendingPinState?: {
		template: any
		formData?: MetadataProperties
		rollupCID?: string
	}
	setPendingSignState: (v: PinnedMetadataProperties) => void
	pendingSignState: PinnedMetadataProperties | undefined
	setSignedEntry: (v: AttestationType | null) => void
	setConfirmationModal: (
		v: {
			title: string
			message: string
			acceptButton: string
			cancelButton: string | null
		} | null,
	) => void
	confirmationModal: {
		title: string
		message: string
		acceptButton: string
		cancelButton: string | null
	} | null
	targetWallet?: TargetWallet | null
}) => {
	if (!leaveSignModalOpen) {
		if (template && template === 'collection') {
			return (
				<CollectionAddComponent
					pendingSign={pendingSignState}
					atomicLoreUrl={atomicLoreUrl}
				/>
			)
		} else {
			return (
				<SignForm
					nft={nft}
					collection={collection}
					origin={origin}
					atomicLoreUrl={atomicLoreUrl}
					setPendingPin={setPendingPinState}
					setPendingSign={setPendingSignState}
					setSignedEntry={setSignedEntry}
					setConfirmationModal={setConfirmationModal}
					template={template}
					matchingAddress={matchingAddress}
					isValidSign={isValidSign}
					pendingPin={pendingPinState}
					pendingSign={pendingSignState}
					countersignEntry={countersignEntry}
					showWallet={true}
					targetWallet={targetWallet}
				/>
			)
		}
	} else {
		return (
			<div className="flex max-w-xl flex-col items-center gap-y-4 ">
				<TypographyHeading
					size="large"
					isBold={true}
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					Exit Attestation Flow?
				</TypographyHeading>
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					All the information you've entered so far will be securely stored in
					the Signatures section in the Profile Manager. You can come back and
					continue signing at any time.
				</TypographyParagraph>
				<div className="flex w-full flex-col items-center gap-y-2">
					<Button
						variant="primary"
						additionalClasses="w-full"
						onClick={() => {
							setPendingPinState(undefined)
							setPendingSignState(undefined)
							onClose()
							setTimeout(() => {
								setLeaveSignModalOpen(false)
								setConfirmationModal(null)
							}, 500)
						}}
					>
						Yes
					</Button>
					{confirmationModal && confirmationModal.cancelButton && (
						<Button
							variant="secondary"
							additionalClasses="w-full"
							onClick={() => setLeaveSignModalOpen(false)}
						>
							Cancel
						</Button>
					)}
				</div>
			</div>
		)
	}
}
